import React, { useEffect } from 'react';

import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { usePostHog } from '@posthog/react';

import {
  AuthProvider,
  useAuth,
} from './contexts/AuthContext';
import {
  ScoringAuthProvider,
  useScoringAuth,
} from './contexts/ScoringAuthContext';
import { VideoUploadProvider } from './contexts/VideoUploadContext';
import DemoApp from './demo/DemoApp';
import AboutPage from './pages/AboutPage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import AddAthletePage from './pages/AddAthletePage';
import ExportAthletesPage from './pages/ExportAthletesPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import ContactPage from './pages/ContactPage';
import CreateAccountPage from './pages/CreateAccountPage';
import FeaturesPage from './pages/FeaturesPage';
import FeaturesPageDraft2 from './pages/FeaturesPageDraft2';
import JoinPage from './pages/JoinPage';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import PricingPage from './pages/PricingPage';
import PrivacyPage from './pages/PrivacyPage';
import RankAthletesPage from './pages/RankAthletesPage';
import ScoreAthletePage from './pages/ScoreAthletePage';
import TermsPage from './pages/TermsPage';
import TryoutAthletesPage from './pages/TryoutAthletesPage';
import TryoutPage from './pages/TryoutPage';
import TryoutSettingsPage from './pages/TryoutSettingsPage';
import TryoutDataExportPage from './pages/TryoutDataExportPage';
import TryoutShell from './pages/TryoutShell';

// Owner-coaches have an email address; scoring coaches sign in via custom token
// with no email. This prevents scoring coaches from accidentally landing on the
// owner dashboard after a successful join.
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { currentUser } = useAuth();
  const isOwner = currentUser !== null && currentUser.email !== null;
  return isOwner ? <>{children}</> : <Navigate to="/login" replace />;
}

// Scoring routes are gated on the scoring-app auth session, which is completely
// separate from the owner-coach session. See agents/dual_firebase_auth.md.
function ScoringRoute({ children }: { children: React.ReactNode }) {
  const { scoringUser } = useScoringAuth();
  return scoringUser !== null ? <>{children}</> : <Navigate to="/join" replace />;
}

function PageTracker() {
  const location = useLocation();
  const posthog = usePostHog();

  useEffect(() => {
    posthog?.capture('$pageview');
  }, [location.pathname, posthog]);

  return null;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/features/draft2" element={<FeaturesPageDraft2 />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/demo/*" element={<DemoApp />} />
      <Route path="/join" element={<JoinPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/createaccount" element={<CreateAccountPage />} />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <AdminDashboardPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/export/:tryoutId"
        element={
          <PrivateRoute>
            <TryoutDataExportPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/tryout/:tryoutId"
        element={
          <ScoringRoute>
            <TryoutShell />
          </ScoringRoute>
        }
      >
        <Route index element={<TryoutPage />} />
        <Route path="athletes" element={<TryoutAthletesPage />} />
        <Route path="athletes/:athleteId" element={<ScoreAthletePage />} />
        <Route path="ranking" element={<RankAthletesPage />} />
        <Route path="add" element={<AddAthletePage />} />
        <Route path="export" element={<ExportAthletesPage />} />
        <Route path="settings" element={<TryoutSettingsPage />} />
        <Route path="announcements" element={<AnnouncementsPage />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScoringAuthProvider>
          <VideoUploadProvider>
            <PageTracker />
            <AppRoutes />
          </VideoUploadProvider>
        </ScoringAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
